import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { Component, effect, input, Signal, TemplateRef, Type } from '@angular/core';
import { ModalStoreSelector } from '@common/selectors/modal.selector';
import { ModalScrollBlockBase } from '@core/base/modalScrollBlock/modal-scroll-block.base';
import { ModalConfig, GetModalInterface } from '@core/services/modals/modals-util.type';

export type BlockingAlertModalConfig = ModalConfig<{
  handle: 'blocking-alert-modal';
  inputs: {
    content?: Signal<TemplateRef<unknown> | undefined> | undefined;
    componentRef?: Signal<Type<unknown> | undefined> | undefined;
  };
}>;

/**
 * BlockingAlertModalComponent
 *
 * This component displays a blocking alert modal with custom content.
 *
 * ## Usage
 *
 * 1. Define a template in your component:
 *
 *    ```html
 *    <ng-template #blockingPopupContent>
 *      <div>
 *        <p>Distribution mode is not available.</p>
 *      </div>
 *    </ng-template>
 *    ```
 *
 * 2. Get a reference to the template in your component class:
 *
 *    ```typescript
 *    blockingPopupContent = viewChild<TemplateRef<any>>('blockingPopupContent');
 *    ```
 *
 * 3. Open the modal and pass the template as content:
 *
 *    ```typescript
 *    this.modalsService.open(BlockingAlertModalComponent.handle, undefined, {
 *      content: this.blockingPopupContent,
 *    });
 *    ```
 *
 * The modal will display the provided template as its content.
 */
@Component({
  selector: 'app-blocking-alert-modal',
  templateUrl: './blocking-alert-modal.component.html',
})
export class BlockingAlertModalComponent
  extends ModalScrollBlockBase
  implements GetModalInterface<BlockingAlertModalConfig>
{
  static handle = 'blocking-alert-modal' as const;

  content = input<Signal<TemplateRef<unknown> | undefined> | undefined>(undefined);
  componentRef = input<Signal<Type<unknown> | undefined> | undefined>(undefined);

  index$: Observable<number> = this.modalSelector
    .selectModal(BlockingAlertModalComponent.handle)
    .pipe(map(el => (el?.index ? el.index : 4)));

  constructor(private modalSelector: ModalStoreSelector) {
    super();
    effect(() => console.log('content', this.content()));
  }
}
